/**
 * @typedef {Object} SignupURLReq
 * @property {string} path_param
 * @property {string} target
 * @property {string} requestType
 * @property {string} payload
 */

/**
 * @typedef {Object} SignupURLRes
 * @property {string} response
 */

/**
 * @typedef {Object} SignupURL
 * @property {string} name
 * @property {string} url
 * @property {string} projectID
 */

/**
 * @typedef {Object} SignInDetail
 * @property {string} signinUrl
 * @property {string} signinEnrollmentToken
 * @property {string} qrCode
 * @property {string} defaultStatus
 */

/**
 * @typedef {Object} GoogleAuthenticationSettings
 * @property {string} googleAuthenticationRequired
 */

/**
 * @typedef {Object} EnterpriseDetails
 * @property {string} name
 * @property {string[]} enabledNotificationTypes
 * @property {string} pubsubTopic
 * @property {string} enterpriseDisplayName
 * @property {SignInDetail[]} signinDetails
 * @property {GoogleAuthenticationSettings} googleAuthenticationSettings
 * @property {string} enterpriseType
 * @property {string} managedGoogleDomainType
 * @property {Object} [contactInfo]
 * @property {string} [contactInfo.dataProtectionOfficerName]
 * @property {string} [contactInfo.dataProtectionOfficerEmail]
 * @property {string} [contactInfo.dataProtectionOfficerPhone]
 * @property {string} [contactInfo.euRepresentativeName]
 * @property {string} [contactInfo.euRepresentativeEmail]
 * @property {string} [contactInfo.euRepresentativePhone]
 * @property {string} [contactInfo.contactEmail]
 */

/**
 * @typedef {Object} EnterpriseResponse
 * @property {string} enterpriseId
 * @property {string} serviceAccount
 * @property {boolean} hasToken
 * @property {string} accountType
 * @property {string|EnterpriseDetails} enterpriseDetails
 * @property {string} enterpriseDisplayName
 * @property {string} enterpriseType
 */

/**
 * @typedef {Object} BaseResponse
 * @property {string} response
 */

// External packages
import base64 from 'react-native-base64';

/**
 * Utility function to decode and parse a base64-encoded response
 * @param {BaseResponse} response
 * @param {function} [transformFn]
 * @returns {any} The decoded and parsed object
 */
export const decodeBase64Response = (response, transformFn) => {
  try {
    if (!response?.response) {
      console.warn('Invalid response format:', response);
      return null;
    }
    
    // Decode the base64 string
    const decodedString = base64.decode(response.response);
    
    // Parse the JSON
    const parsedData = JSON.parse(decodedString);
    
    // Apply transformation if provided
    return transformFn ? transformFn(parsedData) : parsedData;
  } catch (error) {
    console.error('Error decoding response:', error);
    return null;
  }
};

/**
 * Transforms API enterprise response to an EnterpriseResponse object
 * @param {BaseResponse} response
 * @returns {EnterpriseResponse}
 */
export const parseEnterpriseResponse = (response) => {
  return decodeBase64Response(response, (data) => {
    // Create a properly formatted EnterpriseResponse object
    const enterpriseResponse = {
      enterpriseId: data.enterpriseId || '',
      serviceAccount: data.serviceAccount || '',
      hasToken: data.hasToken || false,
      accountType: data.accountType || '',
      enterpriseDetails: data.enterpriseDetails || '{}',
      enterpriseDisplayName: data.enterpriseDisplayName || '',
      enterpriseType: data.enterpriseType || ''
    };
    
    return enterpriseResponse;
  });
};

/**
 * Parses the enterprise details string into an EnterpriseDetails object
 * @param {EnterpriseResponse} enterpriseResponse
 * @returns {EnterpriseDetails|null}
 */
export const parseEnterpriseDetails = (enterpriseResponse) => {
  try {
    if (!enterpriseResponse?.enterpriseDetails) {
      return null;
    }
    
    // If enterpriseDetails is already an object (passed from Main.js), return it directly
    if (typeof enterpriseResponse.enterpriseDetails === 'object') {
      return enterpriseResponse.enterpriseDetails;
    }
    
    // Otherwise, parse it from the string
    return JSON.parse(enterpriseResponse.enterpriseDetails);
  } catch (error) {
    console.error('Error parsing enterprise details:', error);
    return null;
  }
};

/**
 * Full processing of the API response to get both enterprise response and details
 * @param {BaseResponse} apiResponse
 * @returns {{enterpriseResponse: EnterpriseResponse, enterpriseDetails: EnterpriseDetails|null}}
 */
export const processEnterpriseApiResponse = (apiResponse) => {
  const enterpriseResponse = parseEnterpriseResponse(apiResponse);
  const enterpriseDetails = enterpriseResponse ? parseEnterpriseDetails(enterpriseResponse) : null;
  
  // For consistency with how Main.js uses it, also set the enterpriseDetails directly in the enterpriseResponse
  if (enterpriseResponse && enterpriseDetails) {
    enterpriseResponse.enterpriseDetails = enterpriseDetails;
  }
  
  return {
    enterpriseResponse,
    enterpriseDetails
  };
};
